<template>
  <div>
    <p class="font-medium">{{ t('requiredSoonTasks') }}</p>
    <ul
      class="mt-2 grid gap-2 rounded-md bg-slate-50 p-3 md:grid-cols-[auto_minmax(0,1fr)_auto_auto_auto]"
    >
      <li class="hidden text-xs text-hazel-700 md:col-span-5 md:grid md:grid-cols-subgrid">
        <span>{{ t('columnStatus') }}</span>
        <span>{{ t('columnTask') }}</span>
        <span>{{ t('columnLast') }}</span>
        <span>{{ t('columnNext') }}</span>
        <span>{{ t('columnDetails') }}</span>
      </li>
      <li
        v-for="row in rows"
        :key="row.id"
        class="grid grid-cols-1 gap-2 md:col-span-5 md:grid-cols-subgrid md:gap-3"
      >
        <Badge
          :variant="row.isDue ? 'problem' : 'success'"
          size="sm"
          :icon="row.isDue ? AlertTriangleIcon : CircleCheckIcon"
          class="h-6 self-start"
        >
          {{ row.isDue ? t('status.due') : t('status.ok') }}
        </Badge>
        <p class="text-sm font-semibold">{{ row.taskLabel }}</p>
        <p class="text-sm">
          <button
            v-if="row.lastAppointment"
            type="button"
            class="text-primary underline"
            @click="emit('open-workshop-appointment', row.lastAppointment)"
          >
            {{ row.lastDate }}
          </button>
          <span v-else>{{ row.lastDate }}</span>
        </p>
        <p class="text-sm">
          <button
            v-if="row.nextAppointment"
            type="button"
            class="text-primary underline"
            @click="emit('open-workshop-appointment', row.nextAppointment)"
          >
            {{ row.nextDate }}
          </button>
          <span v-else>{{ row.nextDate }}</span>
        </p>
        <div class="grid gap-0.5">
          <div
            v-for="(detail, index) in row.details"
            :key="`${row.id}-detail-${index}`"
            class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
          >
            <span class="text-xs md:text-sm">{{ detail.label }}</span>
            <span class="text-right text-xs tabular-nums md:text-sm">{{ detail.value ?? '' }}</span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import Badge from '@/components/Badge.vue';
import { INSPECTION_WARNING_THRESHOLD_KM } from '@/constants/inspection-warning-threshold-km.constant';
import type { Car } from '@/entities/cars/car.entity';
import { type WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';
import { getNextInspectionDueOdometer } from '@/hooks/use-current-inspection-cycle-mileage';
import { getDateFormat } from '@/hooks/use-date-format';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { getTuvDueDate } from '@/hooks/use-tuv-due-date';
import { useWorkshopAppointments } from '@/queries/use-workshop-appointments';
import { formatFloat } from '@/utils/format-numbers';
import {
  AllocationStatus,
  AllocationType,
  WorkshopAppointmentType,
} from '@carvia/ros-client-types';
import { AlertTriangleIcon, CircleCheckIcon } from 'lucide-vue-next';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface WorkshopDueTaskRow {
  id: string;
  taskLabel: string;
  isDue: boolean;
  lastDate: string;
  lastAppointment?: WorkshopAppointment;
  nextDate: string;
  details: { label: string; value?: string }[];
  nextAppointment?: WorkshopAppointment;
}

const props = defineProps<{
  car: Car;
}>();

const emit = defineEmits<{
  (e: 'open-workshop-appointment', appointment: WorkshopAppointment): void;
}>();

const { t } = useI18n();

function primaryWorkshopAllocation(appointment: WorkshopAppointment) {
  return (
    appointment.allocations?.find((a) => a.type === AllocationType.WORKSHOP_APPOINTMENT) ??
    appointment.allocations?.[0]
  );
}
const formattedLastTuvDate = useFormattedDate(
  computed(() => props.car.lastTuvDate ?? null),
  getDateFormat(),
  '-',
);
const nextRequiredTuvDate = computed(() => {
  if (!props.car.lastTuvDate) return null;
  return DateTime.fromISO(props.car.lastTuvDate).plus({ years: 1 }).endOf('month').toISO();
});
const formattedNextRequiredTuvDate = useFormattedDate(nextRequiredTuvDate, getDateFormat(), '-');
const { data: carAppointmentsData } = useWorkshopAppointments(
  computed(() => ({
    carId: props.car.id,
    type: undefined,
  })),
);
const carAppointments = useFlattenPaginatedData(carAppointmentsData);

const lastCompletedInspectionAppointment = computed(() => {
  const completed = carAppointments.value.filter((a) => {
    if (!a.types.includes(WorkshopAppointmentType.INSPECTION)) return false;
    const alloc = primaryWorkshopAllocation(a);
    return alloc?.status === AllocationStatus.RETURNED && alloc?.milageEnd != null;
  });
  if (!completed.length) return null;
  return (
    completed.sort((a, b) => {
      const endA = primaryWorkshopAllocation(a)?.endDate ?? '';
      const endB = primaryWorkshopAllocation(b)?.endDate ?? '';
      return endB.localeCompare(endA);
    })[0] ?? null
  );
});

const lastCompletedInspectionEndDate = computed(() => {
  const appt = lastCompletedInspectionAppointment.value;
  if (!appt) return null;
  return primaryWorkshopAllocation(appt)?.endDate ?? null;
});

const lastCompletedInspectionMilageEnd = computed(() => {
  const appt = lastCompletedInspectionAppointment.value;
  if (!appt) return null;
  const km = primaryWorkshopAllocation(appt)?.milageEnd;
  if (km == null || Number.isNaN(km)) return null;
  return km;
});

const nextInspectionDueOdometer = computed(() =>
  getNextInspectionDueOdometer(props.car, lastCompletedInspectionMilageEnd.value),
);

const formattedLastInspectionDate = useFormattedDate(
  lastCompletedInspectionEndDate,
  getDateFormat(),
  '-',
);

const activeAppointments = computed(() =>
  carAppointments.value.filter((appointment) => {
    const alloc = primaryWorkshopAllocation(appointment);
    return alloc != null && alloc.status !== AllocationStatus.RETURNED;
  }),
);
const nextPlannedInspectionAppointment = computed(
  () =>
    [...activeAppointments.value]
      .filter((appointment) => appointment.types.includes(WorkshopAppointmentType.INSPECTION))
      .sort((a, b) => {
        const startA = primaryWorkshopAllocation(a)?.startDate ?? '';
        const startB = primaryWorkshopAllocation(b)?.startDate ?? '';
        return startA.localeCompare(startB);
      })[0],
);
const nextPlannedTuvAppointment = computed(
  () =>
    [...activeAppointments.value]
      .filter((appointment) => appointment.types.includes(WorkshopAppointmentType.TUV))
      .sort((a, b) => {
        const startA = primaryWorkshopAllocation(a)?.startDate ?? '';
        const startB = primaryWorkshopAllocation(b)?.startDate ?? '';
        return startA.localeCompare(startB);
      })[0],
);
const hasOpenInspectionAppointment = computed(() =>
  activeAppointments.value.some((appointment) =>
    appointment.types.includes(WorkshopAppointmentType.INSPECTION),
  ),
);
const hasOpenTuvAppointment = computed(() =>
  activeAppointments.value.some((appointment) =>
    appointment.types.includes(WorkshopAppointmentType.TUV),
  ),
);
const isInspectionDue = computed(() => {
  if (hasOpenInspectionAppointment.value) return false;
  const interval = props.car.vehicleType.inspectionIntervalInKm;
  const last = lastCompletedInspectionMilageEnd.value;
  if (last != null && !Number.isNaN(last)) {
    return props.car.milage >= last + interval - INSPECTION_WARNING_THRESHOLD_KM;
  }
  const initialMilage = props.car.initialMilage ?? 0;
  return (
    Math.floor((props.car.milage + INSPECTION_WARNING_THRESHOLD_KM) / interval) * interval >
    initialMilage
  );
});
const isTuvDue = computed(
  () =>
    (!props.car.lastTuvDate ||
      DateTime.fromISO(props.car.lastTuvDate) < DateTime.fromJSDate(getTuvDueDate())) &&
    !hasOpenTuvAppointment.value,
);
const formattedNextPlannedInspectionDate = useFormattedDate(
  computed(() => {
    const appt = nextPlannedInspectionAppointment.value;
    if (!appt) return null;
    return primaryWorkshopAllocation(appt)?.startDate ?? null;
  }),
  getDateFormat(),
  '-',
);
const formattedNextPlannedTuvDate = useFormattedDate(
  computed(() => {
    const appt = nextPlannedTuvAppointment.value;
    if (!appt) return null;
    return primaryWorkshopAllocation(appt)?.startDate ?? null;
  }),
  getDateFormat(),
  '-',
);
const rows = computed<WorkshopDueTaskRow[]>(() => [
  {
    id: 'tuv',
    taskLabel: t('dueTasks.tuv'),
    isDue: isTuvDue.value,
    lastDate: formattedLastTuvDate.value,
    nextDate: formattedNextPlannedTuvDate.value,
    nextAppointment: nextPlannedTuvAppointment.value,
    details: [{ label: t('nextRequiredTuvDate'), value: formattedNextRequiredTuvDate.value }],
  },
  {
    id: 'inspection',
    taskLabel: t('dueTasks.inspection'),
    isDue: isInspectionDue.value,
    lastDate: formattedLastInspectionDate.value,
    lastAppointment: lastCompletedInspectionAppointment.value ?? undefined,
    nextDate: formattedNextPlannedInspectionDate.value,
    nextAppointment: nextPlannedInspectionAppointment.value,
    details: [
      ...(lastCompletedInspectionMilageEnd.value != null
        ? [
            {
              label: t('lastInspectionMileage'),
              value: `${formatFloat(lastCompletedInspectionMilageEnd.value)} km`,
            },
          ]
        : []),
      { label: t('currentMileage'), value: `${formatFloat(props.car.milage)} km` },
      {
        label: t('appointmentDueFor'),
        value: `${formatFloat(nextInspectionDueOdometer.value)} km`,
      },
    ],
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "requiredSoonTasks": "Required soon",
    "columnStatus": "Status",
    "columnTask": "Task",
    "columnLast": "Last",
    "columnNext": "Next",
    "columnDetails": "Details",
    "dueTasks": {
      "inspection": "Inspection check",
      "tuv": "TÜV"
    },
    "nextRequiredTuvDate": "Next required date",
    "currentMileage": "Current mileage",
    "appointmentDueFor": "Next inspection due at",
    "lastInspectionMileage": "Mileage at last inspection",
    "status": {
      "due": "Due",
      "ok": "OK / Planned"
    }
  },
  "de": {
    "requiredSoonTasks": "Bald fällige Aufgaben",
    "columnStatus": "Status",
    "columnTask": "Aufgabe",
    "columnLast": "Letzter",
    "columnNext": "Nächster",
    "columnDetails": "Details",
    "dueTasks": {
      "inspection": "Inspektionsprüfung",
      "tuv": "TÜV"
    },
    "nextRequiredTuvDate": "Nächster Termin fällig am",
    "currentMileage": "Aktueller Kilometerstand",
    "appointmentDueFor": "Nächste Inspektion fällig bei",
    "lastInspectionMileage": "KM bei letzter Inspektion",
    "status": {
      "due": "Fällig",
      "ok": "OK / bereits geplant"
    }
  },
  "es": {
    "requiredSoonTasks": "Próximamente obligatorias",
    "columnStatus": "Estado",
    "columnTask": "Tarea",
    "columnLast": "Última",
    "columnNext": "Próxima",
    "columnDetails": "Detalles",
    "dueTasks": {
      "inspection": "Comprobación de inspección",
      "tuv": "ITV"
    },
    "nextRequiredTuvDate": "Próxima fecha obligatoria",
    "currentMileage": "Kilometraje actual",
    "appointmentDueFor": "Próxima inspección obligatoria a los",
    "lastInspectionMileage": "Kilometraje en la última inspección",
    "status": {
      "due": "Pendiente",
      "ok": "OK / planificado"
    }
  }
}
</i18n>
